import {
	type Coordinates,
	LngLatBounds,
	type LngLatLike,
	type MapGeoJSONFeature
} from 'maplibre-gl';

// GeoJSON allows null geometry despite MapLibre's typing.
type TGeometry = MapGeoJSONFeature['geometry'] | null;

export const getBounds = (feature: MapGeoJSONFeature): LngLatBounds | undefined => {
	const geometry: TGeometry = feature.geometry;

	if (geometry?.type === 'Point') {
		const point = geometry.coordinates as LngLatLike;
		return new LngLatBounds(point, point);
	} else if (geometry?.type === 'LineString') {
		const coordinates = geometry.coordinates as Coordinates;
		const bounds = coordinates.reduce(
			(bounds, coord) => {
				return bounds.extend(coord);
			},
			new LngLatBounds(coordinates[0], coordinates[0])
		);
		return bounds;
	}
};

export const getCenter = (feature: MapGeoJSONFeature): LngLatLike | undefined => {
	return getBounds(feature)?.getCenter();
};

export const parseGeometryLocation = (geometry: TGeometry) => {
	switch (geometry?.type) {
		case 'Point':
			return geometry.coordinates;
		case 'MultiPoint':
			return geometry.coordinates[0];
		case 'LineString':
			return geometry.coordinates[0];
		case 'MultiLineString':
			return geometry.coordinates[0][0];
		case 'Polygon':
			return geometry.coordinates[0][0];
		case 'MultiPolygon':
			return geometry.coordinates[0][0][0];
	}
};

export type Position = ReturnType<typeof parseGeometryLocation>;

export const parseLinestringEndpoints = (geometry: TGeometry): Position[] | undefined => {
	if (geometry?.type === 'LineString') {
		return [geometry.coordinates[0], geometry.coordinates[geometry.coordinates.length - 1]];
	}
	return;
};

export const parseValidPosition = (position: Position) => {
	return position && position[0] && position[1] ? position : null;
};

export const formatPointGeometry = (coordinates: NonNullable<Position>) => {
	const [lng, lat] = coordinates;
	return `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
};
